import React, {useEffect, useState} from 'react';
import {Flex, Text, Button, Input, HTMLDialog} from '@priority-software/priority-style-react';
import texts from '../../../../texts';
import {getGroupTitle} from '../../../../utils';
import {monitorAction} from '../../../../../../services/datadog';
import {HomepageMonitoring} from '../../../../../../constants/monitoring';
import {createDataTestId} from '../../../../../../utils/dataTestId';
import {GROUP_NAME_MAX_LENGTH} from './constants';
import {isEnterKey} from '../../../../../../utils/isEnterKey';
import {homepageStore} from '../../../../store/HomepageStore';
import {DialogToolbarContent} from '../../../../../../components/Dialog/DialogToolbar/DialogToolbar';

type Props = {
    id?: string;
    defaultName?: string;
    onSave: (groupName: string, id?: string) => void;
    onClose: () => void;
};

export const GroupDialog = (props: Props) => {
    const {id, defaultName, onSave, onClose} = props;
    const [groupName, setGroupName] = useState<string>(getGroupTitle(defaultName) || '');
    const inputRef = React.createRef<HTMLInputElement>();

    const saveGroup = async () => {
        if (groupName) {
            await onSave(groupName, id);
            monitorAction(HomepageMonitoring.createGroup, {
                [HomepageMonitoring.groupsCount]: homepageStore.groups.size
            });
        }
    };

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value.length <= GROUP_NAME_MAX_LENGTH) {
            setGroupName(e.target.value);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (isEnterKey(e)) {
            saveGroup();
        }
    };

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, []);

    return (
        <HTMLDialog
            isOpen
            width={520}
            onClose={onClose}
            data-testid="hp-group-dialog"
            toolbarContent={
                <DialogToolbarContent
                    mainTitle={
                        defaultName ? texts.edit_group_dialog_title : texts.add_group_dialog_title
                    }
                />
            }
            content={
                <Flex direction="column" alignItems="flex-start" style={{gap: 16}}>
                    <Text skin="paragraph-1">{texts.add_group_dialog_content}</Text>
                    <Text tag="label" skin="label-1" style={{width: '100%'}}>
                        <Input
                            size="xlarge"
                            onChange={onChange}
                            value={groupName}
                            inputRef={inputRef}
                            onKeyDown={handleKeyDown}
                            {...createDataTestId('group-name-input', true)}
                        />
                    </Text>
                </Flex>
            }
            footer={
                <Flex justifyContent="flex-end" style={{gap: 24}}>
                    <Button skin="secondary" onClick={onClose} size="large" wideButton>
                        {texts.btn_cancel}
                    </Button>

                    <Button
                        skin="primary"
                        onClick={saveGroup}
                        size="large"
                        wideButton
                        disabled={!Boolean(groupName) || groupName === defaultName}
                        {...createDataTestId('hp-save-group-btn', true)}
                    >
                        {texts.save_btn_label}
                    </Button>
                </Flex>
            }
        />
    );
};
